import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { LoginResponse } from '../domain/types';
import { canOpenAdminModule, hasPermission, permissionsOf } from '../domain/formatters';

const BOOKS = [
  {
    id: 'contracts',
    title: 'Contracts',
    job: 'Create, search, and maintain the legal deal.',
    to: '/contracts',
    permission: 'contract:read'
  },
  {
    id: 'commercial',
    title: 'Commercial',
    job: 'Maintain DOI decimals and distribute production volume.',
    to: '/commercial',
    permission: 'contract:read'
  },
  {
    id: 'accounting',
    title: 'Accounting',
    job: 'Period close, books, and the Citation month walkthrough.',
    to: '/accounting/demo',
    permission: 'doi:read'
  }
] as const;

interface ModuleHomePageProps {
  auth?: LoginResponse;
}

export function ModuleHomePage({ auth }: ModuleHomePageProps): ReactElement {
  const perms = auth ? permissionsOf(auth) : undefined;
  const role = auth?.role;
  const showProcurement =
    role !== 'ContractAdmin' && role !== 'DivisionOrder' && role !== 'Controller';

  return (
    <section className="module-home" aria-label="Modules">
      <h2 className="module-home-title">Modules</h2>
      <p className="module-home-lead">Choose where to work. Each module owns its own pages.</p>
      <div className="module-home-grid">
        {BOOKS.filter((mod) => hasPermission(perms, mod.permission, role)).map((mod) => (
          <Link
            key={mod.id}
            to={mod.to}
            className="module-home-tile module-home-tile--live"
            data-testid={`module-tile-${mod.id}`}
          >
            <span className="module-home-tile-name">{mod.title}</span>
            <span className="module-home-tile-job">{mod.job}</span>
            <span className="module-home-tile-status module-home-tile-status--live">Open</span>
          </Link>
        ))}
        {showProcurement && (
          <Link
            to="/requests"
            className="module-home-tile module-home-tile--live"
            data-testid="module-tile-procurement"
          >
            <span className="module-home-tile-name">Procurement</span>
            <span className="module-home-tile-job">Request, quote, buy, stock, and move materials.</span>
            <span className="module-home-tile-status module-home-tile-status--live">Open</span>
          </Link>
        )}
        {role && canOpenAdminModule(role) && (
          <Link
            to="/admin"
            className="module-home-tile module-home-tile--live"
            data-testid="module-tile-admin"
          >
            <span className="module-home-tile-name">Admin</span>
            <span className="module-home-tile-job">Users, roles, regions, numbering, and system options.</span>
            <span className="module-home-tile-status module-home-tile-status--live">Open</span>
          </Link>
        )}
      </div>
    </section>
  );
}
